import {fetchPhotoBlob} from './warehousePhotos';

/**
 * Кэш объектных URL фотографий.
 *
 * Нужен из-за мигания при листании галереи: тело фото приходится забирать fetch'ем
 * (эндпоинт закрыт заголовком Authorization, см. warehousePhotos), а каждый вызов
 * createObjectURL даёт новую ссылку. Даже когда ответ лежит в HTTP-кэше, между сменой
 * кадра и готовностью новой ссылки проходит хотя бы один кадр отрисовки — и на нём
 * виден скелетон. При листании вперёд-назад это читается как моргание экрана.
 *
 * Поэтому ссылки переиспользуются: один раз загруженное фото показывается мгновенно.
 * Ограничение по числу записей обязательно — объектный URL держит блоб в памяти
 * вкладки, пока его не отозвали, и без вытеснения галерея на сотню снимков
 * превратилась бы в утечку.
 */

/**
 * Сколько фото держим одновременно. При 24 выдача (двадцать обложек) и галерея склада
 * перекрывались, и вытеснение отзывало ссылку у показанного кадра. Запас двукратный.
 */
const MAX_ENTRIES = 64;

/**
 * Внешний адрес фотографии — тот, что уже несёт схему. Свои файлы приходят
 * относительным путём и закрыты заголовком Authorization, из карточки маркетплейса —
 * готовым `https://…`, который тянет сам браузер. Вид адреса определяем в одном месте.
 */
export function isExternalPhotoSrc(src: string): boolean {
	return /^(?:https?:|blob:|data:)/.test(src);
}

/**
 * Прогрев соседнего кадра галереи: человек жмёт стрелку и ждать не должен. Ошибки
 * проглочены намеренно — это упреждение, а неудача лишь вернёт загрузку на переход.
 */
export function prefetchPhoto(src: string): void {
	if (isExternalPhotoSrc(src)) {
		new Image().src = src;

		return;
	}

	void loadPhotoObjectUrl(src).catch(() => undefined);
}

/** Порядок вставки в Map — порядок обращения: при попадании запись переносится в конец. */
const cache = new Map<string, string>();

/** Незавершённые загрузки: два компонента с одним фото не должны качать его дважды. */
const inFlight = new Map<string, Promise<string>>();

/** Кому сообщить, что ссылка по адресу устарела: показывать её дальше нельзя. */
const listeners = new Map<string, Set<() => void>>();

/**
 * Адреса, у которых тело сменилось. Следующая загрузка каждого пойдёт мимо кэша
 * браузера: ответ помечен `max-age`, и обычный запрос вернул бы прежний файл —
 * тот самый, от которого мы только что избавились.
 */
const stale = new Set<string>();

/** Сколько раз тело по адресу заменяли: по нему видно, что ответ опоздал. */
const generations = new Map<string, number>();

/** Готовая ссылка, если фото уже загружали. Синхронно — чтобы не мигать скелетоном. */
export function peekPhotoObjectUrl(url: string): string | undefined {
	const hit = cache.get(url);

	if (hit === undefined) {
		return undefined;
	}

	// Обновляем позицию: только что использованное вытесняется последним
	cache.delete(url);
	cache.set(url, hit);

	return hit;
}

export async function loadPhotoObjectUrl(url: string): Promise<string> {
	const hit = peekPhotoObjectUrl(url);

	if (hit !== undefined) {
		return hit;
	}

	const running = inFlight.get(url);

	if (running) {
		return running;
	}

	const reload = stale.delete(url);
	// Поколение адреса на момент запроса: пока он летит, тело могли заменить
	const generation = generations.get(url) ?? 0;

	const request = fetchPhotoBlob(url, {reload})
		.then((blob) => {
			const objectUrl = URL.createObjectURL(blob);

			// Ответ опоздал: в кэш он уже не годится — там лежит кадр посвежее
			if ((generations.get(url) ?? 0) !== generation) {
				URL.revokeObjectURL(objectUrl);

				return loadPhotoObjectUrl(url);
			}

			store(url, objectUrl);

			return objectUrl;
		})
		.catch((error: unknown) => {
			// Пометку возвращаем: иначе следующая попытка возьмёт из кэша браузера
			// прежний файл — тот самый, ради замены которого её и ставили
			if (reload) {
				stale.add(url);
			}

			throw error;
		})
		.finally(() => {
			// Только свою запись: инвалидация могла завести новую загрузку поверх
			if (inFlight.get(url) === request) {
				inFlight.delete(url);
			}
		});

	inFlight.set(url, request);

	return request;
}

/**
 * Ссылка протухла, а тело по адресу прежнее: забываем её и просим заново.
 *
 * Отдельно от invalidatePhoto — та объявляет, что файл заменили, и зовёт подписчиков.
 * Здесь звать нельзя: соседний кадр на том же адресе сбросил бы себе счётчик попыток,
 * и два компонента гоняли бы перезапрос по кругу.
 */
export async function renewPhotoObjectUrl(url: string): Promise<string> {
	forget(url);

	return loadPhotoObjectUrl(url);
}

/**
 * Следить за адресом: слушателя зовут, когда тело по нему сменилось. Одного сброса кэша
 * мало — показанный кадр живёт в состоянии компонента и сам ни от чего не перерисуется.
 * Возвращает отписку.
 */
export function subscribePhoto(url: string, listener: () => void): () => void {
	const forUrl = listeners.get(url) ?? new Set<() => void>();

	forUrl.add(listener);
	listeners.set(url, forUrl);

	return () => {
		forUrl.delete(listener);

		if (forUrl.size === 0) {
			listeners.delete(url);
		}
	};
}

/**
 * Тело по адресу заменили: забыть кадр и сказать об этом тем, кто его показывает.
 *
 * Нужно там, где за одним адресом со временем стоят разные файлы. Такой адрес в кабинете
 * один — фото сотрудника (`/api/warehouses/members/:id/photo`): у остальных в адресе
 * стоит id самого файла. Бэкенд отдаёт кадр сотрудника с коротким `max-age`
 * без `immutable`, а наш кэш живёт до вытеснения.
 */
export function invalidatePhoto(url: string): void {
	forget(url);
	stale.add(url);
	generations.set(url, (generations.get(url) ?? 0) + 1);

	// Копией, а не самим набором: слушатель вправе отписаться прямо в обработчике
	for (const listener of [...(listeners.get(url) ?? [])]) {
		listener();
	}
}

/** Положить ссылку, отозвав прежнюю: без этого затёртый блоб остаётся в памяти вкладки. */
function store(url: string, objectUrl: string): void {
	const previous = cache.get(url);

	if (previous && previous !== objectUrl) {
		URL.revokeObjectURL(previous);
	}

	cache.set(url, objectUrl);
	evict();
}

/** Убрать ссылку из кэша, отозвав её: держать протухший объектный URL незачем. */
function forget(url: string): void {
	const objectUrl = cache.get(url);

	cache.delete(url);
	inFlight.delete(url);

	if (objectUrl) {
		URL.revokeObjectURL(objectUrl);
	}
}

/** Вытеснение самых давних записей с отзывом ссылки: без revoke блоб остаётся в памяти. */
function evict(): void {
	while (cache.size > MAX_ENTRIES) {
		const oldest = cache.keys().next();

		if (oldest.done) {
			return;
		}

		const objectUrl = cache.get(oldest.value);

		cache.delete(oldest.value);

		if (objectUrl) {
			URL.revokeObjectURL(objectUrl);
		}
	}
}
